import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { Button, Select, Table, Tag, message } from 'antd'
import styled from 'styled-components'
import useIsBlocked from 'src/hooks/useIsBlocked'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import {
  listVenueProductIssues,
  updateVenueProductIssueStatus,
} from 'src/utils/productIssueApi'
import {
  PRODUCT_ISSUE_SCOPE,
  PRODUCT_ISSUE_STATUS,
  buildProductIssuesListQuery,
  getProductIssueCategoryLabel,
  getProductIssueRoutingLabel,
  getProductIssueScopeLabel,
  getProductIssueStatusLabel,
  getProductIssueSubjectDetail,
  getProductIssueSubjectLabel,
  getProductIssuesEmptyText,
  resolveProductIssueScope,
} from 'src/utils/productIssue'

const { Option } = Select

const statusColor = {
  reported: 'orange',
  in_progress: 'blue',
  resolved: 'green',
}

const ProductIssues = ({ venueId }) => {
  const blocked = useIsBlocked('product-issues')
  const location = useLocation()
  const history = useHistory()

  const { view, issueType } = useMemo(() => {
    const params = new URLSearchParams(location.search || '')
    const nextView = params.get('view') === 'resolved' ? 'resolved' : 'open'
    const rawType = params.get('issueType')
    const nextType =
      rawType === PRODUCT_ISSUE_SCOPE.PRODUCT ||
      rawType === PRODUCT_ISSUE_SCOPE.PRICE_FILE
        ? rawType
        : 'all'
    return { view: nextView, issueType: nextType }
  }, [location.search])

  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [updatingId, setUpdatingId] = useState(null)

  const load = useCallback(async () => {
    if (!venueId) return
    setLoading(true)
    try {
      const data = await listVenueProductIssues(
        venueId,
        buildProductIssuesListQuery({ view, issueType }),
      )
      setItems(data?.items || [])
    } catch (error) {
      message.error(error.message || 'Could not load product issues')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [venueId, view, issueType])

  useEffect(() => {
    void load()
  }, [load])

  if (blocked) {
    return <PermissionBlockScreen />
  }

  const pushFilters = ({ nextView = view, nextIssueType = issueType } = {}) => {
    const base = `/v/${venueId}/purchasing/product-issues`
    const params = new URLSearchParams()
    if (nextView === 'resolved') params.set('view', 'resolved')
    if (nextIssueType && nextIssueType !== 'all') {
      params.set('issueType', nextIssueType)
    }
    const qs = params.toString()
    history.push(qs ? `${base}?${qs}` : base)
  }

  const handleStatusChange = async (issueId, status) => {
    setUpdatingId(issueId)
    try {
      await updateVenueProductIssueStatus(venueId, issueId, { status })
      message.success('Status updated')
      await load()
    } catch (error) {
      message.error(error.message || 'Could not update status')
    } finally {
      setUpdatingId(null)
    }
  }

  const columns = [
    {
      title: 'Reported',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => (value ? new Date(value).toLocaleString('en-GB') : '—'),
      width: 160,
    },
    {
      title: 'Supplier',
      dataIndex: 'supplierTitle',
      key: 'supplierTitle',
      ellipsis: true,
    },
    {
      title: 'Issue type',
      key: 'scope',
      width: 110,
      render: (_, row) => {
        const scope = resolveProductIssueScope(row)
        return (
          <Tag
            color={scope === PRODUCT_ISSUE_SCOPE.PRICE_FILE ? 'purple' : 'blue'}
          >
            {getProductIssueScopeLabel(scope)}
          </Tag>
        )
      },
    },
    {
      title: 'Product / catalogue',
      key: 'product',
      render: (_, row) => (
        <div>
          <div>{getProductIssueSubjectLabel(row)}</div>
          <Muted>{getProductIssueSubjectDetail(row)}</Muted>
        </div>
      ),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (value, row) =>
        getProductIssueCategoryLabel(value, resolveProductIssueScope(row)),
    },
    {
      title: 'Explanation',
      dataIndex: 'explanation',
      key: 'explanation',
      ellipsis: true,
      render: value => value || '—',
    },
    {
      title: 'Routed',
      key: 'routing',
      width: 110,
      render: (_, row) => {
        const routing = getProductIssueRoutingLabel(row)
        return (
          <Tag color={routing.color} title={routing.title || undefined}>
            {routing.label}
          </Tag>
        )
      },
    },
    {
      title: 'Reported by',
      dataIndex: 'reportedByName',
      key: 'reportedByName',
      render: value => value || '—',
    },
    {
      title: 'Status',
      key: 'status',
      width: 180,
      render: (_, row) => (
        <Select
          size='small'
          value={row.status}
          style={{ width: 150 }}
          loading={updatingId === row._id}
          onChange={status => handleStatusChange(row._id, status)}
        >
          <Option value={PRODUCT_ISSUE_STATUS.REPORTED}>
            {getProductIssueStatusLabel(PRODUCT_ISSUE_STATUS.REPORTED)}
          </Option>
          <Option value={PRODUCT_ISSUE_STATUS.IN_PROGRESS}>
            {getProductIssueStatusLabel(PRODUCT_ISSUE_STATUS.IN_PROGRESS)}
          </Option>
          <Option value={PRODUCT_ISSUE_STATUS.RESOLVED}>
            {getProductIssueStatusLabel(PRODUCT_ISSUE_STATUS.RESOLVED)}
          </Option>
        </Select>
      ),
    },
    {
      title: '',
      key: 'badge',
      width: 100,
      render: (_, row) => (
        <Tag color={statusColor[row.status] || 'default'}>
          {getProductIssueStatusLabel(row.status)}
        </Tag>
      ),
    },
  ]

  return (
    <Wrapper>
      <HeaderRow>
        <div>
          <h2 style={{ margin: 0 }}>Product Issues</h2>
          <Muted>
            Product and whole price-file catalogue issues reported from OMS.
          </Muted>
        </div>
        <Filters>
          <ButtonGroup>
            <Button
              type={view === 'open' ? 'primary' : 'default'}
              onClick={() => pushFilters({ nextView: 'open' })}
            >
              Open
            </Button>
            <Button
              type={view === 'resolved' ? 'primary' : 'default'}
              onClick={() => pushFilters({ nextView: 'resolved' })}
            >
              Resolved
            </Button>
          </ButtonGroup>
          <ButtonGroup data-testid='product-issues-type-filter'>
            <Button
              type={issueType === 'all' ? 'primary' : 'default'}
              onClick={() => pushFilters({ nextIssueType: 'all' })}
            >
              All
            </Button>
            <Button
              type={
                issueType === PRODUCT_ISSUE_SCOPE.PRODUCT
                  ? 'primary'
                  : 'default'
              }
              onClick={() =>
                pushFilters({ nextIssueType: PRODUCT_ISSUE_SCOPE.PRODUCT })
              }
            >
              Product Issues
            </Button>
            <Button
              type={
                issueType === PRODUCT_ISSUE_SCOPE.PRICE_FILE
                  ? 'primary'
                  : 'default'
              }
              onClick={() =>
                pushFilters({ nextIssueType: PRODUCT_ISSUE_SCOPE.PRICE_FILE })
              }
            >
              Price File Issues
            </Button>
          </ButtonGroup>
        </Filters>
      </HeaderRow>

      <Table
        rowKey={row => row._id}
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={{ pageSize: 25 }}
        scroll={{ x: 1200 }}
        locale={{
          emptyText: getProductIssuesEmptyText({ view, issueType }),
        }}
      />
    </Wrapper>
  )
}

const Wrapper = styled.div`
  padding: 24px;
  background: #fff;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`

const Filters = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;
`

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`

const Muted = styled.div`
  color: #8c8c8c;
  font-size: 12px;
  margin-top: 4px;
`

export default ProductIssues
